import React, { useMemo, useEffect, useCallback } from "react";
import ReactPlayer, { ReactPlayerProps } from "react-player";
import { observer } from "mobx-react-lite";

import { videoPlayerService } from "../../services/video-player/VideoPlayerService";

interface PictureInPictureVideoProps extends ReactPlayerProps {
  url: string;
}

export default observer(function PictureInPictureVideo({ url }: PictureInPictureVideoProps) {
  const {
    pictureInPictureVideo,
    isVideoPlaying,
    stopVideo,
    startVideo,
    jumpNext,
    jumpPrevious,
    handleProgress,
    isPictureInPicture,
    cancelDetachVideo,
    videoProgress,
    videoDuration,
    timestamps,
    currentTimestamp,
  } = videoPlayerService;

  const handleProgressPictureInPicture = useMemo(
    () => (isPictureInPicture ? handleProgress : () => {}),
    [handleProgress, isPictureInPicture]
  );

  const handlePlayPictureInPicture = useMemo(
    () => (isPictureInPicture ? startVideo : () => {}),
    [isPictureInPicture, startVideo]
  );

  const handlePausePictureInPicture = useMemo(
    () => (isPictureInPicture ? stopVideo : () => {}),
    [isPictureInPicture, stopVideo]
  );

  const jumpPreviousCallback = useCallback(() => jumpPrevious(), [jumpPrevious]);
  const jumpNextCallback = useCallback(() => jumpNext(), [jumpNext]);

  useEffect(() => {
    const thereAreTimestamps = timestamps?.items.length;
    const videoTimeIsAfterLastTimestamp = timestamps?.isTimeAfterLastTimestamp(videoProgress);
    const videoTimeIsBeforeFirstTimestamp =
      timestamps?.isTimeBeforeFirstTimestamp(videoProgress) ||
      timestamps?.isFirstTimeStamp(currentTimestamp);

    if (videoDuration !== 0 && thereAreTimestamps && !videoTimeIsAfterLastTimestamp) {
      navigator.mediaSession?.setActionHandler("nexttrack", jumpNextCallback);
    }
    if (videoDuration !== 0 && thereAreTimestamps && !videoTimeIsBeforeFirstTimestamp) {
      navigator.mediaSession?.setActionHandler("previoustrack", jumpPreviousCallback);
    }
    return () => {
      navigator.mediaSession?.setActionHandler("previoustrack", null);
      navigator.mediaSession?.setActionHandler("nexttrack", null);
    };
  }, [
    currentTimestamp,
    jumpNextCallback,
    jumpPreviousCallback,
    timestamps,
    videoDuration,
    videoProgress,
  ]);

  return (
    <ReactPlayer
      ref={pictureInPictureVideo}
      url={url}
      style={{ display: "none" }}
      playing={isVideoPlaying}
      onProgress={handleProgressPictureInPicture}
      pip={isPictureInPicture}
      onDisablePIP={cancelDetachVideo}
      onPlay={handlePlayPictureInPicture}
      onPause={handlePausePictureInPicture}
    />
  );
});
